precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform float uBass;
uniform float uMid;
uniform float uHigh;
uniform float uAudioAverage;
uniform float uZoom;
uniform vec2 uOffset;
uniform vec3 uColorPrimary;
uniform vec3 uColorSecondary;
uniform vec3 uColorAccent;
uniform vec2 uDeviceTilt;

varying vec2 vUv;

const int MAX_ITERATIONS = 256;

vec3 palette(float t, vec3 a, vec3 b, vec3 c, vec3 d) {
  return a + b * cos(6.28318 * (c * t + d));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution.xy) / min(uResolution.x, uResolution.y);
  
  // Apply zoom and offset - focusing on an interesting edge
  // Add subtle device tilt influence
  float zoom = pow(1.5, uZoom);
  vec2 tiltOffset = uDeviceTilt * (1.0 / zoom); // Tilt effect scales with zoom
  vec2 c = uv / zoom + uOffset + tiltOffset;
  
  vec2 z = vec2(0.0);
  float iterations = 0.0;
  
  // Audio-reactive iteration modifier (stronger)
  float iterMod = 1.0 + uBass * 0.3;
  
  for (int i = 0; i < MAX_ITERATIONS; i++) {
    if (dot(z, z) > 4.0) break;
    
    // z = z^2 + c with audio-reactive distortion (stronger)
    float xTemp = z.x * z.x - z.y * z.y + c.x;
    z.y = 2.0 * z.x * z.y + c.y + sin(uTime * 0.1 + float(i) * 0.01) * uMid * 0.005;
    z.x = xTemp + cos(uTime * 0.15) * uHigh * 0.002;
    
    iterations += iterMod;
  }
  
  // Smooth coloring
  if (dot(z, z) > 4.0) {
    float log_zn = log(dot(z, z)) / 2.0;
    float nu = log(log_zn / log(2.0)) / log(2.0);
    iterations = iterations - nu;
  }
  
  // Normalize iterations
  float t = iterations / float(MAX_ITERATIONS);
  
  // Create sophisticated color palette
  vec3 col;
  
  if (t >= 1.0) {
    // Inside the set - deep void
    col = uColorPrimary * (0.3 + uBass * 0.2);
  } else {
    // Outside - gradient based on escape time
    float phase = t * 3.0 + uTime * 0.05;
    
    vec3 col1 = uColorPrimary;
    vec3 col2 = uColorSecondary;
    vec3 col3 = uColorAccent;
    
    // Multi-stop gradient
    if (t < 0.3) {
      col = mix(col1, col2, t / 0.3);
    } else if (t < 0.6) {
      col = mix(col2, col3, (t - 0.3) / 0.3);
    } else {
      col = mix(col3, col1, (t - 0.6) / 0.4);
    }
    
    // Add glow based on audio (stronger)
    col += uColorAccent * uHigh * 0.4 * (1.0 - t);
    col += uColorSecondary * uBass * 0.2;
    
    // Add depth with iteration-based shading
    col *= 0.7 + 0.3 * sin(phase * 6.28318);
  }
  
  // Subtle vignette
  vec2 vigUv = vUv * 2.0 - 1.0;
  float vignette = 1.0 - dot(vigUv, vigUv) * 0.3;
  col *= vignette;
  
  // Gamma correction
  col = pow(col, vec3(0.85));
  
  gl_FragColor = vec4(col, 1.0);
}

